import type { AvatarTemplate } from '../types/avatar';
import { asset } from '../lib/assets';

// ──────────────────────────────────────────────
// Shared Geometry & Drawing Helpers
// ──────────────────────────────────────────────

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number | number[]
) {
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, radius);
  } else {
    const r = typeof radius === 'number' ? radius : radius[0] || 0;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }
}

/** Draw subtle diagonal stripe texture overlay for depth */
function drawStripeTexture(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
  spacing = 24,
  lineWidth = 1,
  alpha = 0.06
) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  for (let i = -h; i < w + h; i += spacing) {
    ctx.moveTo(x + i, y);
    ctx.lineTo(x + i - h, y + h);
  }
  ctx.stroke();
  ctx.restore();
}

/** Draw subtle dot pattern for textured surfaces */
function drawDotTexture(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
  spacing = 22,
  dotR = 1.2,
  alpha = 0.05
) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  for (let dy = 0; dy < h; dy += spacing) {
    const offset = (Math.floor(dy / spacing) % 2) * (spacing / 2);
    for (let dx = 0; dx < w; dx += spacing) {
      ctx.beginPath();
      ctx.arc(x + dx + offset, y + dy, dotR, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

/** Draw a decorative corner accent */
function drawCornerAccents(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
  len = 28,
  lineWidth = 3
) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';

  // Top-left
  ctx.beginPath();
  ctx.moveTo(x, y + len);
  ctx.lineTo(x, y);
  ctx.lineTo(x + len, y);
  ctx.stroke();

  // Top-right
  ctx.beginPath();
  ctx.moveTo(x + w - len, y);
  ctx.lineTo(x + w, y);
  ctx.lineTo(x + w, y + len);
  ctx.stroke();

  // Bottom-left
  ctx.beginPath();
  ctx.moveTo(x, y + h - len);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x + len, y + h);
  ctx.stroke();

  // Bottom-right
  ctx.beginPath();
  ctx.moveTo(x + w - len, y + h);
  ctx.lineTo(x + w, y + h);
  ctx.lineTo(x + w, y + h - len);
  ctx.stroke();

  ctx.restore();
}

// ──────────────────────────────────────────────
// Logo preloading & handling
// ──────────────────────────────────────────────
let _logoImg: HTMLImageElement | null = null;
let _logoLoaded = false;

export function getLogoImage(): HTMLImageElement | null {
  if (_logoLoaded && _logoImg) return _logoImg;
  if (!_logoImg) {
    _logoImg = new Image();
    _logoImg.crossOrigin = 'anonymous';
    _logoImg.onload = () => {
      _logoLoaded = true;
    };
    _logoImg.onerror = () => {
      _logoImg = null;
    };
    _logoImg.src = asset('images/traianologo.png');
  }
  return _logoLoaded ? _logoImg : null;
}

// Preload immediately
getLogoImage();

// ──────────────────────────────────────────────
// Reusable Bottom Brand Renderer
// ──────────────────────────────────────────────

interface BrandBannerOptions {
  mode?: 'text' | 'logo';
  bannerW: number;
  bannerH: number;
  bannerX: number;
  bannerY: number;
  tagText?: string;
  tagColor?: string;
  tagTextColor?: string;
  subtitleText?: string;
  borderColor?: string;
}

function drawBrandBanner(
  ctx: CanvasRenderingContext2D,
  options: BrandBannerOptions
) {
  const {
    mode = 'text',
    bannerW,
    bannerH,
    bannerX,
    bannerY,
    tagText = 'EU VOTO',
    tagColor = '#123ee0',
    tagTextColor = '#ffffff',
    subtitleText = 'DEPUTADO ESTADUAL',
    borderColor = '#18e908',
  } = options;

  const cx = bannerX + bannerW / 2;

  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // 1. Shadow for depth
  ctx.shadowColor = 'rgba(2, 13, 40, 0.65)';
  ctx.shadowBlur = 32;
  ctx.shadowOffsetY = 12;

  // 2. Banner Background: Rich subtle linear gradient
  const bgGrad = ctx.createLinearGradient(bannerX, bannerY, bannerX + bannerW, bannerY + bannerH);
  bgGrad.addColorStop(0, '#0c2c84');
  bgGrad.addColorStop(0.45, '#06194d');
  bgGrad.addColorStop(1, '#030e2c');

  ctx.fillStyle = bgGrad;
  ctx.beginPath();
  roundRect(ctx, bannerX, bannerY, bannerW, bannerH, 30);
  ctx.fill();

  // 3. Border with subtle gradient
  ctx.shadowColor = 'transparent';
  ctx.lineWidth = 6;
  ctx.strokeStyle = borderColor;
  ctx.stroke();

  // 4. Subtle glass highlight rim inside the top edge
  ctx.save();
  ctx.beginPath();
  roundRect(ctx, bannerX + 4, bannerY + 3, bannerW - 8, bannerH - 6, 26);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
  ctx.stroke();
  ctx.restore();

  // 5. Texture overlay
  drawStripeTexture(ctx, bannerX, bannerY, bannerW, bannerH, '#ffffff', 20, 1, 0.04);

  const logo = getLogoImage();

  if (mode === 'logo' && logo && logo.complete && logo.naturalWidth > 0) {
    // ─── LOGO MODE ───
    const logoAspect = logo.naturalWidth / logo.naturalHeight;
    const maxW = bannerW - 60;
    const maxH = bannerH - 24;
    let drawW = maxW;
    let drawH = drawW / logoAspect;

    if (drawH > maxH) {
      drawH = maxH;
      drawW = drawH * logoAspect;
    }

    const drawX = cx - drawW / 2;
    const drawY = bannerY + (bannerH - drawH) / 2;

    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = 20;
    ctx.shadowOffsetY = 6;
    ctx.drawImage(logo, drawX, drawY, drawW, drawH);
    ctx.shadowColor = 'transparent';
  } else {
    // ─── TYPOGRAPHIC TEXT MODE ───
    // Top tag pill with dynamic width based on text length
    if (tagText) {
      ctx.font = '700 22px "Clash Display", Arial, sans-serif';
      const textW = ctx.measureText(tagText).width;
      const tagW = Math.max(180, textW + 52); // Generous padding so text never overflows
      const tagH = 44;
      const tagX = cx - tagW / 2;
      const tagY = bannerY - 22;

      ctx.shadowColor = 'rgba(0,0,0,0.3)';
      ctx.shadowBlur = 12;
      ctx.shadowOffsetY = 4;

      const tagGrad = ctx.createLinearGradient(tagX, tagY, tagX, tagY + tagH);
      tagGrad.addColorStop(0, tagColor);
      tagGrad.addColorStop(1, '#09257a');

      ctx.fillStyle = tagGrad;
      ctx.beginPath();
      roundRect(ctx, tagX, tagY, tagW, tagH, 16);
      ctx.fill();

      ctx.shadowColor = 'transparent';
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(255,255,255,0.2)';
      ctx.stroke();

      ctx.fillStyle = tagTextColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(tagText, cx, tagY + 22);
    }

    // TRAIANO 55789 – Perfectly Centered
    const isShortBanner = bannerH < 180;
    const nameY = isShortBanner ? bannerY + 76 : bannerY + 88;

    // Measure both words to compute exact combined center
    ctx.font = '700 72px "Clash Display", "Arial Black", sans-serif';
    const traianoWidth = ctx.measureText('TRAIANO').width;
    ctx.font = '700 78px "Clash Display", "Arial Black", sans-serif';
    const numWidth = ctx.measureText('55789').width;
    const gap = 20;
    const totalW = traianoWidth + gap + numWidth;
    const startX = cx - totalW / 2;

    // Draw TRAIANO
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 72px "Clash Display", "Arial Black", sans-serif';
    ctx.shadowColor = 'rgba(0,0,0,0.4)';
    ctx.shadowBlur = 14;
    ctx.shadowOffsetY = 4;
    ctx.fillText('TRAIANO', startX, nameY);

    // Draw 55789 with vibrant green gradient fill
    const numGrad = ctx.createLinearGradient(startX + traianoWidth + gap, nameY - 40, startX + traianoWidth + gap, nameY + 40);
    numGrad.addColorStop(0, '#4ffb40');
    numGrad.addColorStop(0.5, '#18e908');
    numGrad.addColorStop(1, '#10ab04');

    ctx.fillStyle = numGrad;
    ctx.font = '700 78px "Clash Display", "Arial Black", sans-serif';
    ctx.fillText('55789', startX + traianoWidth + gap, nameY);
    ctx.shadowColor = 'transparent';

    // Subtitle
    if (subtitleText) {
      ctx.textAlign = 'center';
      ctx.fillStyle = '#cbd7e8';
      ctx.font = '600 26px "Clash Display", "Switzer", Arial, sans-serif';
      ctx.fillText(subtitleText, cx, isShortBanner ? bannerY + 128 : bannerY + 144);
    }
  }

  ctx.restore();
}

// ──────────────────────────────────────────────
// TEMPLATES DEFINITIONS
// ──────────────────────────────────────────────

export const avatarTemplates: AvatarTemplate[] = [
  // ==========================================
  // MODELO 1: AVATAR 1:1 – EU VOTO TRAIANO
  // ==========================================
  {
    id: 'avatar-01',
    name: 'Modelo 1',
    category: 'avatar',
    width: 1080,
    height: 1080,
    badgeTitle: 'Eu Voto 55789',
    tagline: 'Eu Voto Traiano',
    badgeColor: '#18e908',
    accentColor: '#123ee0',
    styleVariant: 'circle-badge',
    renderOverlay: (ctx, w, h, assets) => {
      const cx = w / 2;
      const cy = h / 2;
      const outerR = 480;
      const innerR = 455;
      const mode = assets?.logoMode || 'text';

      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // 1. Outer Dark Overlay Mask with Smooth Radial Gradient
      const maskGrad = ctx.createRadialGradient(cx, cy - 60, innerR * 0.5, cx, cy, w * 0.72);
      maskGrad.addColorStop(0, '#0c2e82');
      maskGrad.addColorStop(0.45, '#06194d');
      maskGrad.addColorStop(1, '#020d28');

      ctx.fillStyle = maskGrad;
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2, true);
      ctx.fill();

      // Antialiased seam ring
      ctx.beginPath();
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = '#06194d';
      ctx.stroke();

      // Subtle texture on outer mask area
      drawDotTexture(ctx, 0, 0, w, h, '#ffffff', 20, 1.2, 0.04);

      // 2. Circular Outer Rings with Sleek Gradients
      const blueGrad = ctx.createLinearGradient(cx - outerR, cy - outerR, cx + outerR, cy + outerR);
      blueGrad.addColorStop(0, '#2e63ff');
      blueGrad.addColorStop(0.5, '#123ee0');
      blueGrad.addColorStop(1, '#082672');

      ctx.beginPath();
      ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
      ctx.lineWidth = 14;
      ctx.strokeStyle = blueGrad;
      ctx.stroke();

      const greenGrad = ctx.createLinearGradient(cx - innerR, cy - innerR, cx + innerR, cy + innerR);
      greenGrad.addColorStop(0, '#45fa35');
      greenGrad.addColorStop(0.5, '#18e908');
      greenGrad.addColorStop(1, '#0fa405');

      ctx.beginPath();
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
      ctx.lineWidth = 20;
      ctx.strokeStyle = greenGrad;
      ctx.stroke();

      // Inner subtle glow ring
      ctx.beginPath();
      ctx.arc(cx, cy, innerR - 12, 0, Math.PI * 2);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(255,255,255,0.16)';
      ctx.stroke();

      // 3. Top-Right Floating Badge: "EU VOTO 55789"
      const badgeW = 260;
      const badgeH = 135;
      const badgeX = cx + 180;
      const badgeY = cy - 470;

      ctx.shadowColor = 'rgba(2, 13, 40, 0.55)';
      ctx.shadowBlur = 24;
      ctx.shadowOffsetY = 10;

      const badgeBgGrad = ctx.createLinearGradient(badgeX, badgeY, badgeX + badgeW, badgeY + badgeH);
      badgeBgGrad.addColorStop(0, '#0c2e82');
      badgeBgGrad.addColorStop(1, '#030e2c');

      ctx.fillStyle = badgeBgGrad;
      ctx.beginPath();
      roundRect(ctx, badgeX, badgeY, badgeW, badgeH, 22);
      ctx.fill();

      ctx.shadowColor = 'transparent';
      ctx.lineWidth = 5;
      ctx.strokeStyle = greenGrad;
      ctx.stroke();

      drawStripeTexture(ctx, badgeX, badgeY, badgeW, badgeH, '#ffffff', 14, 1, 0.05);

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.fillStyle = '#ffffff';
      ctx.font = '700 28px "Clash Display", Arial, sans-serif';
      ctx.fillText('EU VOTO', badgeX + badgeW / 2, badgeY + 40);

      ctx.fillStyle = greenGrad;
      ctx.font = '700 56px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('55789', badgeX + badgeW / 2, badgeY + 94);

      // 4. Bottom Prominent Brand Banner (Supporting Logo or Text)
      const bannerW = 760;
      const bannerH = 185;
      const bannerX = cx - bannerW / 2;
      const bannerY = h - 225;

      drawBrandBanner(ctx, {
        mode,
        bannerW,
        bannerH,
        bannerX,
        bannerY,
        tagText: 'EU VOTO',
        tagColor: '#123ee0',
        subtitleText: 'DEPUTADO ESTADUAL',
        borderColor: greenGrad as unknown as string,
      });

      ctx.restore();
    },
  },

  // ==========================================
  // MODELO 2: AVATAR 1:1 – PALMAS COM TRAIANO
  // ==========================================
  {
    id: 'avatar-02',
    name: 'Modelo 2',
    category: 'avatar',
    width: 1080,
    height: 1080,
    badgeTitle: 'Palmas com Traiano',
    tagline: 'Palmas com Traiano 55789',
    badgeColor: '#123ee0',
    accentColor: '#18e908',
    styleVariant: 'circle-frame',
    renderOverlay: (ctx, w, h, assets) => {
      const cx = w / 2;
      const cy = h / 2;
      const innerR = 455;
      const mode = assets?.logoMode || 'text';

      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Outer radial gradient mask
      const maskGrad = ctx.createRadialGradient(cx, cy - 60, innerR * 0.45, cx, cy, w * 0.72);
      maskGrad.addColorStop(0, '#103698');
      maskGrad.addColorStop(0.5, '#06194d');
      maskGrad.addColorStop(1, '#020b24');

      ctx.fillStyle = maskGrad;
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2, true);
      ctx.fill();

      // Antialiased seam
      ctx.beginPath();
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = '#06194d';
      ctx.stroke();

      drawDotTexture(ctx, 0, 0, w, h, '#ffffff', 18, 1.1, 0.05);

      // Dual-tone circular frame
      const greenGrad = ctx.createLinearGradient(0, 0, w, h);
      greenGrad.addColorStop(0, '#45fa35');
      greenGrad.addColorStop(0.5, '#18e908');
      greenGrad.addColorStop(1, '#10aa05');

      ctx.beginPath();
      ctx.arc(cx, cy, 475, 0, Math.PI * 2);
      ctx.lineWidth = 20;
      ctx.strokeStyle = greenGrad;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
      ctx.lineWidth = 16;
      ctx.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx.stroke();

      // Subtle inner glow
      ctx.beginPath();
      ctx.arc(cx, cy, innerR - 10, 0, Math.PI * 2);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.stroke();

      // Top Left badge: "PALMAS É 55789"
      const badgeW = 270;
      const badgeH = 130;
      const badgeX = cx - 440;
      const badgeY = cy - 460;

      ctx.shadowColor = 'rgba(0,0,0,0.45)';
      ctx.shadowBlur = 20;
      ctx.shadowOffsetY = 8;

      const badgeGrad = ctx.createLinearGradient(badgeX, badgeY, badgeX + badgeW, badgeY + badgeH);
      badgeGrad.addColorStop(0, '#2153f3');
      badgeGrad.addColorStop(1, '#0c2e82');

      ctx.fillStyle = badgeGrad;
      ctx.beginPath();
      roundRect(ctx, badgeX, badgeY, badgeW, badgeH, 24);
      ctx.fill();

      ctx.shadowColor = 'transparent';
      ctx.lineWidth = 5;
      ctx.strokeStyle = '#d2f74a';
      ctx.stroke();

      drawStripeTexture(ctx, badgeX, badgeY, badgeW, badgeH, '#ffffff', 14, 1, 0.05);

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.fillStyle = '#ffffff';
      ctx.font = '700 24px "Clash Display", Arial, sans-serif';
      ctx.fillText('PALMAS É', badgeX + badgeW / 2, badgeY + 38);

      ctx.fillStyle = '#d2f74a';
      ctx.font = '700 50px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('55789', badgeX + badgeW / 2, badgeY + 88);

      // Bottom Brand Banner
      const bannerW = 780;
      const bannerH = 185;
      const bannerX = cx - bannerW / 2;
      const bannerY = h - 225;

      drawBrandBanner(ctx, {
        mode,
        bannerW,
        bannerH,
        bannerX,
        bannerY,
        tagText: 'PALMAS COM',
        tagColor: '#16a53a',
        subtitleText: 'DEPUTADO ESTADUAL · PSD 55',
        borderColor: '#2e63ff',
      });

      ctx.restore();
    },
  },

  // ==========================================
  // MODELO 3: AVATAR 1:1 – JUNTOS PELO PARANÁ
  // ==========================================
  {
    id: 'avatar-03',
    name: 'Modelo 3',
    category: 'avatar',
    width: 1080,
    height: 1080,
    badgeTitle: 'Juntos pelo Paraná',
    tagline: 'Juntos por um Paraná Melhor',
    badgeColor: '#d2f74a',
    accentColor: '#123ee0',
    styleVariant: 'circle-split',
    renderOverlay: (ctx, w, h, assets) => {
      const cx = w / 2;
      const cy = h / 2;
      const innerR = 455;
      const mode = assets?.logoMode || 'text';

      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Outer radial gradient mask
      const maskGrad = ctx.createRadialGradient(cx, cy - 60, innerR * 0.45, cx, cy, w * 0.72);
      maskGrad.addColorStop(0, '#0e2f88');
      maskGrad.addColorStop(0.5, '#06194d');
      maskGrad.addColorStop(1, '#020d28');

      ctx.fillStyle = maskGrad;
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2, true);
      ctx.fill();

      // Antialiased seam
      ctx.beginPath();
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = '#06194d';
      ctx.stroke();

      drawDotTexture(ctx, 0, 0, w, h, '#ffffff', 20, 1.2, 0.04);

      // Multi-stop sweeping gradient circular border
      const sweepGrad = ctx.createLinearGradient(0, 0, w, h);
      sweepGrad.addColorStop(0, '#42fa32');
      sweepGrad.addColorStop(0.35, '#18e908');
      sweepGrad.addColorStop(0.65, '#2e63ff');
      sweepGrad.addColorStop(1, '#d2f74a');

      ctx.beginPath();
      ctx.arc(cx, cy, 475, 0, Math.PI * 2);
      ctx.lineWidth = 22;
      ctx.strokeStyle = sweepGrad;
      ctx.stroke();

      // Inner glow ring
      ctx.beginPath();
      ctx.arc(cx, cy, innerR - 8, 0, Math.PI * 2);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(210,247,74,0.18)';
      ctx.stroke();

      // Badge top right: "JUNTOS PELO PARANÁ 55789"
      const badgeW = 290;
      const badgeH = 135;
      const badgeX = cx + 150;
      const badgeY = cy - 470;

      ctx.shadowColor = 'rgba(0,0,0,0.45)';
      ctx.shadowBlur = 24;
      ctx.shadowOffsetY = 10;

      const badgeGrad = ctx.createLinearGradient(badgeX, badgeY, badgeX + badgeW, badgeY + badgeH);
      badgeGrad.addColorStop(0, '#3efa2a');
      badgeGrad.addColorStop(1, '#15b806');

      ctx.fillStyle = badgeGrad;
      ctx.beginPath();
      roundRect(ctx, badgeX, badgeY, badgeW, badgeH, 22);
      ctx.fill();

      ctx.shadowColor = 'transparent';
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.stroke();

      drawStripeTexture(ctx, badgeX, badgeY, badgeW, badgeH, '#06194d', 14, 1, 0.08);

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.fillStyle = '#06194d';
      ctx.font = '700 22px "Clash Display", Arial, sans-serif';
      ctx.fillText('JUNTOS PELO PARANÁ', badgeX + badgeW / 2, badgeY + 40);

      ctx.font = '700 56px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('55789', badgeX + badgeW / 2, badgeY + 92);

      // Bottom Brand Banner with "PARANÁ MELHOR" (now with dynamic wide pill tag)
      const bannerW = 820;
      const bannerH = 190;
      const bannerX = cx - bannerW / 2;
      const bannerY = h - 230;

      drawBrandBanner(ctx, {
        mode,
        bannerW,
        bannerH,
        bannerX,
        bannerY,
        tagText: 'PARANÁ MELHOR',
        tagColor: '#123ee0',
        subtitleText: 'DEPUTADO ESTADUAL',
        borderColor: '#d2f74a',
      });

      ctx.restore();
    },
  },

  // ==========================================
  // MODELO 4: AVATAR 1:1 – LOGO OFICIAL
  // ==========================================
  {
    id: 'avatar-04',
    name: 'Modelo 4',
    category: 'avatar',
    width: 1080,
    height: 1080,
    badgeTitle: 'Logo Oficial',
    tagline: 'Traiano 55789 – Logo Oficial',
    badgeColor: '#06194d',
    accentColor: '#18e908',
    styleVariant: 'circle-logo',
    renderOverlay: (ctx, w, h, assets) => {
      const cx = w / 2;
      const cy = h / 2;
      const innerR = 460;
      const mode = assets?.logoMode || 'logo';

      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Outer radial gradient mask
      const maskGrad = ctx.createRadialGradient(cx, cy - 60, innerR * 0.45, cx, cy, w * 0.75);
      maskGrad.addColorStop(0, '#0d2d80');
      maskGrad.addColorStop(0.5, '#06194d');
      maskGrad.addColorStop(1, '#020b22');

      ctx.fillStyle = maskGrad;
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2, true);
      ctx.fill();

      // Antialiased seam
      ctx.beginPath();
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = '#06194d';
      ctx.stroke();

      drawDotTexture(ctx, 0, 0, w, h, '#123ee0', 16, 1.4, 0.06);

      // Triple ring border with gradients
      ctx.beginPath();
      ctx.arc(cx, cy, 490, 0, Math.PI * 2);
      ctx.lineWidth = 8;
      ctx.strokeStyle = '#06194d';
      ctx.stroke();

      const greenGrad = ctx.createLinearGradient(0, 0, w, h);
      greenGrad.addColorStop(0, '#42fa32');
      greenGrad.addColorStop(0.5, '#18e908');
      greenGrad.addColorStop(1, '#0ea404');

      ctx.beginPath();
      ctx.arc(cx, cy, 478, 0, Math.PI * 2);
      ctx.lineWidth = 18;
      ctx.strokeStyle = greenGrad;
      ctx.stroke();

      const blueGrad = ctx.createLinearGradient(0, 0, w, h);
      blueGrad.addColorStop(0, '#2e63ff');
      blueGrad.addColorStop(1, '#0e3498');

      ctx.beginPath();
      ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
      ctx.lineWidth = 12;
      ctx.strokeStyle = blueGrad;
      ctx.stroke();

      // Corner accents on the outer mask
      drawCornerAccents(ctx, 24, 24, w - 48, h - 48, '#18e908', 50, 4);

      // Top-left small vote badge
      const vBadgeW = 200;
      const vBadgeH = 96;
      const vBadgeX = 35;
      const vBadgeY = 35;

      ctx.shadowColor = 'rgba(0,0,0,0.35)';
      ctx.shadowBlur = 16;
      ctx.shadowOffsetY = 6;

      ctx.fillStyle = greenGrad;
      ctx.beginPath();
      roundRect(ctx, vBadgeX, vBadgeY, vBadgeW, vBadgeH, 20);
      ctx.fill();
      ctx.shadowColor = 'transparent';

      drawStripeTexture(ctx, vBadgeX, vBadgeY, vBadgeW, vBadgeH, '#06194d', 12, 1, 0.08);

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#06194d';
      ctx.font = '700 22px "Clash Display", Arial, sans-serif';
      ctx.fillText('EU VOTO', vBadgeX + vBadgeW / 2, vBadgeY + 30);
      ctx.font = '700 44px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('55789', vBadgeX + vBadgeW / 2, vBadgeY + 68);

      // Bottom Brand Banner (Supports both Logo and Text)
      const bannerW = 760;
      const bannerH = 185;
      const bannerX = cx - bannerW / 2;
      const bannerY = h - 225;

      drawBrandBanner(ctx, {
        mode,
        bannerW,
        bannerH,
        bannerX,
        bannerY,
        tagText: 'OFICIAL',
        tagColor: '#123ee0',
        subtitleText: 'DEPUTADO ESTADUAL',
        borderColor: greenGrad as unknown as string,
      });

      ctx.restore();
    },
  },

  // ==========================================
  // CARD 1: REDES SOCIAIS (4:5) – APOIO OFICIAL
  // ==========================================
  {
    id: 'card-01',
    name: 'Card 1',
    category: 'card',
    width: 1080,
    height: 1350,
    badgeTitle: 'Post de Apoio Oficial',
    tagline: 'Eu Voto Traiano 55789',
    badgeColor: '#18e908',
    accentColor: '#123ee0',
    styleVariant: 'card-editorial',
    renderOverlay: (ctx, w, h, assets) => {
      const mode = assets?.logoMode || 'text';

      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // 1. Top Header Bar with rich subtle gradient
      const topH = 140;
      const topGrad = ctx.createLinearGradient(0, 0, w, 0);
      topGrad.addColorStop(0, '#0c2e82');
      topGrad.addColorStop(0.5, '#06194d');
      topGrad.addColorStop(1, '#030e2c');

      ctx.fillStyle = topGrad;
      ctx.fillRect(0, 0, w, topH);

      drawStripeTexture(ctx, 0, 0, w, topH, '#ffffff', 16, 1, 0.04);

      // Gradient accent line below header
      const lineGrad = ctx.createLinearGradient(0, 0, w, 0);
      lineGrad.addColorStop(0, '#42fa32');
      lineGrad.addColorStop(0.5, '#18e908');
      lineGrad.addColorStop(1, '#d2f74a');

      ctx.fillStyle = lineGrad;
      ctx.fillRect(0, topH - 8, w, 8);

      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';

      // Header title
      ctx.fillStyle = '#ffffff';
      ctx.font = '700 40px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('TRAIANO', 60, 68);

      ctx.fillStyle = '#18e908';
      ctx.font = '700 44px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('55789', 270, 68);

      ctx.fillStyle = '#d2f74a';
      ctx.font = '600 24px "Clash Display", Arial, sans-serif';
      ctx.fillText('· DEPUTADO ESTADUAL', 440, 68);

      // Top right badge sticker
      const badgeW = 230;
      const badgeH = 120;
      const badgeX = w - badgeW - 60;
      const badgeY = 165;

      ctx.shadowColor = 'rgba(0,0,0,0.4)';
      ctx.shadowBlur = 20;
      ctx.shadowOffsetY = 8;

      const badgeGrad = ctx.createLinearGradient(badgeX, badgeY, badgeX + badgeW, badgeY + badgeH);
      badgeGrad.addColorStop(0, '#3efa2a');
      badgeGrad.addColorStop(1, '#12b805');

      ctx.fillStyle = badgeGrad;
      ctx.beginPath();
      roundRect(ctx, badgeX, badgeY, badgeW, badgeH, 20);
      ctx.fill();

      ctx.shadowColor = 'transparent';
      drawStripeTexture(ctx, badgeX, badgeY, badgeW, badgeH, '#06194d', 14, 1, 0.07);

      ctx.textAlign = 'center';
      ctx.fillStyle = '#06194d';
      ctx.font = '700 26px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('EU VOTO', badgeX + badgeW / 2, badgeY + 36);

      ctx.font = '700 52px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('55789', badgeX + badgeW / 2, badgeY + 84);

      // 2. Bottom Mega Footer Banner
      const botH = 260;
      const botY = h - botH;

      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 30;
      ctx.shadowOffsetY = -10;

      const botBgGrad = ctx.createLinearGradient(0, botY, 0, h);
      botBgGrad.addColorStop(0, '#0b2978');
      botBgGrad.addColorStop(0.5, '#06194d');
      botBgGrad.addColorStop(1, '#020d28');

      ctx.fillStyle = botBgGrad;
      ctx.fillRect(0, botY, w, botH);

      ctx.fillStyle = lineGrad;
      ctx.fillRect(0, botY, w, 10);

      ctx.shadowColor = 'transparent';
      drawStripeTexture(ctx, 0, botY, w, botH, '#ffffff', 20, 1, 0.04);

      const logo = getLogoImage();

      if (mode === 'logo' && logo && logo.complete && logo.naturalWidth > 0) {
        const logoAspect = logo.naturalWidth / logo.naturalHeight;
        const maxW = 620;
        const maxH = botH - 50;
        let drawW = maxW;
        let drawH = drawW / logoAspect;
        if (drawH > maxH) {
          drawH = maxH;
          drawW = drawH * logoAspect;
        }

        const drawX = w / 2 - drawW / 2;
        const drawY = botY + (botH - drawH) / 2 + 6;

        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 24;
        ctx.shadowOffsetY = 8;
        ctx.drawImage(logo, drawX, drawY, drawW, drawH);
        ctx.shadowColor = 'transparent';
      } else {
        // Slogan Tag with dynamic measurement and generous padding
        const sloganText = 'JUNTOS POR UM PARANÁ MELHOR';
        ctx.font = '700 22px "Clash Display", Arial, sans-serif';
        const sloganTextW = ctx.measureText(sloganText).width;
        const tagW = Math.max(380, sloganTextW + 54);
        const tagH = 46;
        const tagX = 60;
        const tagY = botY + 28;

        ctx.fillStyle = '#123ee0';
        ctx.beginPath();
        roundRect(ctx, tagX, tagY, tagW, tagH, 14);
        ctx.fill();

        ctx.textAlign = 'center';
        ctx.fillStyle = '#ffffff';
        ctx.fillText(sloganText, tagX + tagW / 2, tagY + 24);

        // Name & Number
        ctx.textAlign = 'left';
        ctx.fillStyle = '#ffffff';
        ctx.font = '700 78px "Clash Display", "Arial Black", sans-serif';
        ctx.fillText('TRAIANO', 60, botY + 140);

        ctx.fillStyle = '#18e908';
        ctx.font = '700 84px "Clash Display", "Arial Black", sans-serif';
        ctx.fillText('55789', 530, botY + 140);

        // Legal / cargo footer
        ctx.fillStyle = '#cbd7e8';
        ctx.font = '600 28px "Clash Display", "Switzer", Arial, sans-serif';
        ctx.fillText('DEPUTADO ESTADUAL · PSD 55', 60, botY + 208);
      }

      ctx.restore();
    },
  },

  // ==========================================
  // CARD 2: REDES SOCIAIS (4:5) – PALMAS AVANÇA
  // ==========================================
  {
    id: 'card-02',
    name: 'Card 2',
    category: 'card',
    width: 1080,
    height: 1350,
    badgeTitle: 'Palmas Segue Avançando',
    tagline: 'Palmas Segue Avançando',
    badgeColor: '#123ee0',
    accentColor: '#d2f74a',
    styleVariant: 'card-banner',
    renderOverlay: (ctx, w, h, assets) => {
      const mode = assets?.logoMode || 'text';

      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Top Banner with vibrant gradient
      const topH = 160;
      const grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0, '#06194d');
      grad.addColorStop(0.5, '#123ee0');
      grad.addColorStop(1, '#092a82');

      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, topH);

      drawStripeTexture(ctx, 0, 0, w, topH, '#ffffff', 18, 1, 0.05);

      ctx.fillStyle = '#d2f74a';
      ctx.fillRect(0, topH - 8, w, 8);

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.fillStyle = '#d2f74a';
      ctx.font = '700 26px "Clash Display", Arial, sans-serif';
      ctx.fillText('PALMAS SEGUE AVANÇANDO', w / 2, 48);

      ctx.fillStyle = '#ffffff';
      ctx.font = '700 50px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('COM TRAIANO 55789', w / 2, 102);

      // Floating badge
      const badgeW = 240;
      const badgeH = 130;
      const badgeX = w - badgeW - 60;
      const badgeY = 190;

      ctx.shadowColor = 'rgba(0,0,0,0.35)';
      ctx.shadowBlur = 18;
      ctx.shadowOffsetY = 6;

      const bGrad = ctx.createLinearGradient(badgeX, badgeY, badgeX + badgeW, badgeY + badgeH);
      bGrad.addColorStop(0, '#2e63ff');
      bGrad.addColorStop(1, '#103698');

      ctx.fillStyle = bGrad;
      ctx.beginPath();
      roundRect(ctx, badgeX, badgeY, badgeW, badgeH, 20);
      ctx.fill();

      ctx.lineWidth = 6;
      ctx.strokeStyle = '#18e908';
      ctx.stroke();

      ctx.shadowColor = 'transparent';
      drawStripeTexture(ctx, badgeX, badgeY, badgeW, badgeH, '#ffffff', 14, 1, 0.06);

      ctx.fillStyle = '#ffffff';
      ctx.font = '700 24px "Clash Display", Arial, sans-serif';
      ctx.fillText('TÔ COM', badgeX + badgeW / 2, badgeY + 38);

      ctx.fillStyle = '#18e908';
      ctx.font = '700 52px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('55789', badgeX + badgeW / 2, badgeY + 86);

      // Bottom Banner
      const botH = 240;
      const botY = h - botH;

      ctx.shadowColor = 'rgba(0,0,0,0.45)';
      ctx.shadowBlur = 24;
      ctx.shadowOffsetY = -8;

      const botGrad = ctx.createLinearGradient(0, botY, 0, h);
      botGrad.addColorStop(0, '#0c2e82');
      botGrad.addColorStop(1, '#020b22');

      ctx.fillStyle = botGrad;
      ctx.fillRect(0, botY, w, botH);

      ctx.fillStyle = '#18e908';
      ctx.fillRect(0, botY, w, 10);

      ctx.shadowColor = 'transparent';
      drawStripeTexture(ctx, 0, botY, w, botH, '#ffffff', 22, 1, 0.04);

      const logo = getLogoImage();

      if (mode === 'logo' && logo && logo.complete && logo.naturalWidth > 0) {
        const logoAspect = logo.naturalWidth / logo.naturalHeight;
        const maxW = 600;
        const maxH = botH - 40;
        let drawW = maxW;
        let drawH = drawW / logoAspect;
        if (drawH > maxH) {
          drawH = maxH;
          drawW = drawH * logoAspect;
        }

        const drawX = w / 2 - drawW / 2;
        const drawY = botY + (botH - drawH) / 2 + 4;

        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 24;
        ctx.shadowOffsetY = 8;
        ctx.drawImage(logo, drawX, drawY, drawW, drawH);
        ctx.shadowColor = 'transparent';
      } else {
        // Centered TRAIANO 55789
        ctx.font = '700 76px "Clash Display", "Arial Black", sans-serif';
        const traianoW = ctx.measureText('TRAIANO').width;
        ctx.font = '700 82px "Clash Display", "Arial Black", sans-serif';
        const numW = ctx.measureText('55789').width;
        const gap = 24;
        const totalW = traianoW + gap + numW;
        const startX = w / 2 - totalW / 2;

        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#ffffff';
        ctx.font = '700 76px "Clash Display", "Arial Black", sans-serif';
        ctx.fillText('TRAIANO', startX, botY + 98);

        ctx.fillStyle = '#18e908';
        ctx.font = '700 82px "Clash Display", "Arial Black", sans-serif';
        ctx.fillText('55789', startX + traianoW + gap, botY + 98);

        ctx.textAlign = 'center';
        ctx.fillStyle = '#d2f74a';
        ctx.font = '600 28px "Clash Display", "Switzer", Arial, sans-serif';
        ctx.fillText('DEPUTADO ESTADUAL · PALMAS NO CORAÇÃO', w / 2, botY + 175);
      }

      ctx.restore();
    },
  },

  // ==========================================
  // CARD 3: REDES SOCIAIS (4:5) – MINIMAL EDITORIAL
  // ==========================================
  {
    id: 'card-03',
    name: 'Card 3',
    category: 'card',
    width: 1080,
    height: 1350,
    badgeTitle: 'Paraná Cada Vez Mais Forte',
    tagline: 'Trabalho e Resultados',
    badgeColor: '#18e908',
    accentColor: '#123ee0',
    styleVariant: 'card-clean',
    renderOverlay: (ctx, w, h, assets) => {
      const mode = assets?.logoMode || 'text';

      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Top badge left with gradient and dynamic padding
      const topBadgeText = 'PARANÁ MAIS FORTE';
      ctx.font = '700 24px "Clash Display", Arial, sans-serif';
      const topBadgeTextW = ctx.measureText(topBadgeText).width;
      const topBadgeW = Math.max(340, topBadgeTextW + 54);
      const topBadgeH = 90;
      const topBadgeX = 60;
      const topBadgeY = 60;

      ctx.shadowColor = 'rgba(0,0,0,0.4)';
      ctx.shadowBlur = 18;
      ctx.shadowOffsetY = 8;

      const tbGrad = ctx.createLinearGradient(topBadgeX, topBadgeY, topBadgeX + topBadgeW, topBadgeY + topBadgeH);
      tbGrad.addColorStop(0, '#0c2e82');
      tbGrad.addColorStop(1, '#06194d');

      ctx.fillStyle = tbGrad;
      ctx.beginPath();
      roundRect(ctx, topBadgeX, topBadgeY, topBadgeW, topBadgeH, 20);
      ctx.fill();

      ctx.lineWidth = 5;
      ctx.strokeStyle = '#18e908';
      ctx.stroke();

      ctx.shadowColor = 'transparent';
      drawStripeTexture(ctx, topBadgeX, topBadgeY, topBadgeW, topBadgeH, '#ffffff', 14, 1, 0.05);

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#ffffff';
      ctx.font = '700 24px "Clash Display", Arial, sans-serif';
      ctx.fillText(topBadgeText, topBadgeX + topBadgeW / 2, topBadgeY + 45);

      // Top right vote tag
      const rightBadgeW = 220;
      const rightBadgeH = 110;
      const rightBadgeX = w - rightBadgeW - 60;
      const rightBadgeY = 60;

      ctx.shadowColor = 'rgba(0,0,0,0.4)';
      ctx.shadowBlur = 18;
      ctx.shadowOffsetY = 8;

      const rbGrad = ctx.createLinearGradient(rightBadgeX, rightBadgeY, rightBadgeX + rightBadgeW, rightBadgeY + rightBadgeH);
      rbGrad.addColorStop(0, '#2e63ff');
      rbGrad.addColorStop(1, '#0c2e82');

      ctx.fillStyle = rbGrad;
      ctx.beginPath();
      roundRect(ctx, rightBadgeX, rightBadgeY, rightBadgeW, rightBadgeH, 20);
      ctx.fill();

      ctx.lineWidth = 5;
      ctx.strokeStyle = '#d2f74a';
      ctx.stroke();

      ctx.shadowColor = 'transparent';
      drawStripeTexture(ctx, rightBadgeX, rightBadgeY, rightBadgeW, rightBadgeH, '#ffffff', 12, 1, 0.06);

      ctx.fillStyle = '#ffffff';
      ctx.font = '700 20px "Clash Display", Arial, sans-serif';
      ctx.fillText('EU VOTO', rightBadgeX + rightBadgeW / 2, rightBadgeY + 34);

      ctx.fillStyle = '#d2f74a';
      ctx.font = '700 48px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('55789', rightBadgeX + rightBadgeW / 2, rightBadgeY + 76);

      // Bottom floating modern box
      const boxW = w - 120;
      const boxH = 220;
      const boxX = 60;
      const boxY = h - boxH - 60;

      ctx.shadowColor = 'rgba(2, 13, 40, 0.7)';
      ctx.shadowBlur = 32;
      ctx.shadowOffsetY = 12;

      const boxGrad = ctx.createLinearGradient(boxX, boxY, boxX + boxW, boxY + boxH);
      boxGrad.addColorStop(0, 'rgba(12, 46, 130, 0.96)');
      boxGrad.addColorStop(0.5, 'rgba(6, 25, 77, 0.96)');
      boxGrad.addColorStop(1, 'rgba(2, 13, 40, 0.98)');

      ctx.fillStyle = boxGrad;
      ctx.beginPath();
      roundRect(ctx, boxX, boxY, boxW, boxH, 30);
      ctx.fill();

      ctx.lineWidth = 6;
      ctx.strokeStyle = '#18e908';
      ctx.stroke();

      ctx.shadowColor = 'transparent';
      drawStripeTexture(ctx, boxX, boxY, boxW, boxH, '#ffffff', 20, 1, 0.04);
      drawCornerAccents(ctx, boxX + 16, boxY + 16, boxW - 32, boxH - 32, '#18e908', 24, 2);

      const logo = getLogoImage();

      if (mode === 'logo' && logo && logo.complete && logo.naturalWidth > 0) {
        const logoAspect = logo.naturalWidth / logo.naturalHeight;
        const maxW = boxW - 80;
        const maxH = boxH - 30;
        let drawW = maxW;
        let drawH = drawW / logoAspect;
        if (drawH > maxH) {
          drawH = maxH;
          drawW = drawH * logoAspect;
        }

        const drawX = boxX + (boxW - drawW) / 2;
        const drawY = boxY + (boxH - drawH) / 2;

        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 20;
        ctx.shadowOffsetY = 6;
        ctx.drawImage(logo, drawX, drawY, drawW, drawH);
        ctx.shadowColor = 'transparent';
      } else {
        ctx.textAlign = 'left';
        ctx.fillStyle = '#ffffff';
        ctx.font = '700 76px "Clash Display", "Arial Black", sans-serif';
        ctx.fillText('TRAIANO', boxX + 60, boxY + 86);

        const traianoW = ctx.measureText('TRAIANO').width;

        ctx.fillStyle = '#18e908';
        ctx.font = '700 84px "Clash Display", "Arial Black", sans-serif';
        ctx.fillText('55789', boxX + 60 + traianoW + 28, boxY + 86);

        ctx.fillStyle = '#ffffff';
        ctx.font = '600 26px "Clash Display", "Switzer", Arial, sans-serif';
        ctx.fillText('DEPUTADO ESTADUAL', boxX + 60, boxY + 155);

        ctx.fillStyle = '#d2f74a';
        ctx.font = '700 26px "Clash Display", Arial, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText('PSD 55', boxX + boxW - 60, boxY + 155);
      }

      ctx.restore();
    },
  },

  // ==========================================
  // CARD 4: REDES SOCIAIS (4:5) – LOGO OFICIAL
  // ==========================================
  {
    id: 'card-04',
    name: 'Card 4',
    category: 'card',
    width: 1080,
    height: 1350,
    badgeTitle: 'Logo Oficial',
    tagline: 'Card com Logo Oficial',
    badgeColor: '#06194d',
    accentColor: '#18e908',
    styleVariant: 'card-logo',
    renderOverlay: (ctx, w, h, assets) => {
      const mode = assets?.logoMode || 'logo';

      ctx.save();
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Top Header Bar with gradient
      const topH = 120;
      const topGrad = ctx.createLinearGradient(0, 0, w, 0);
      topGrad.addColorStop(0, '#0c2e82');
      topGrad.addColorStop(0.5, '#06194d');
      topGrad.addColorStop(1, '#020d28');

      ctx.fillStyle = topGrad;
      ctx.fillRect(0, 0, w, topH);
      drawStripeTexture(ctx, 0, 0, w, topH, '#ffffff', 16, 1, 0.04);

      // Green accent line
      ctx.fillStyle = '#18e908';
      ctx.fillRect(0, topH - 6, w, 6);

      // Header text
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#ffffff';
      ctx.font = '700 32px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('EU APOIO', w / 2 - 100, topH / 2);
      ctx.fillStyle = '#18e908';
      ctx.font = '700 36px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('55789', w / 2 + 100, topH / 2);

      // Corner accents on full canvas
      drawCornerAccents(ctx, 30, topH + 20, w - 60, h - topH - 40, '#18e908', 40, 3);

      // Bottom Footer
      const botH = 300;
      const botY = h - botH;

      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 30;
      ctx.shadowOffsetY = -8;

      const botGrad = ctx.createLinearGradient(0, botY, 0, h);
      botGrad.addColorStop(0, 'rgba(6, 25, 77, 0.0)');
      botGrad.addColorStop(0.25, 'rgba(12, 46, 130, 0.88)');
      botGrad.addColorStop(1, 'rgba(2, 13, 40, 0.98)');

      ctx.fillStyle = botGrad;
      ctx.fillRect(0, botY, w, botH);

      ctx.shadowColor = 'transparent';

      const logo = getLogoImage();

      if (mode === 'logo' && logo && logo.complete && logo.naturalWidth > 0) {
        const logoAspect = logo.naturalWidth / logo.naturalHeight;
        const maxW = 650;
        const maxH = maxW / logoAspect;
        const logoX = w / 2 - maxW / 2;
        const logoY = h - maxH - 30;

        ctx.shadowColor = 'rgba(0,0,0,0.45)';
        ctx.shadowBlur = 24;
        ctx.shadowOffsetY = 8;
        ctx.drawImage(logo, logoX, logoY, maxW, maxH);
        ctx.shadowColor = 'transparent';
      } else {
        // Centered TRAIANO 55789
        ctx.font = '700 76px "Clash Display", "Arial Black", sans-serif';
        const traianoW = ctx.measureText('TRAIANO').width;
        ctx.font = '700 82px "Clash Display", "Arial Black", sans-serif';
        const numW = ctx.measureText('55789').width;
        const gap = 24;
        const totalW = traianoW + gap + numW;
        const startX = w / 2 - totalW / 2;

        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#ffffff';
        ctx.font = '700 76px "Clash Display", "Arial Black", sans-serif';
        ctx.fillText('TRAIANO', startX, h - 120);

        ctx.fillStyle = '#18e908';
        ctx.font = '700 82px "Clash Display", "Arial Black", sans-serif';
        ctx.fillText('55789', startX + traianoW + gap, h - 120);

        ctx.textAlign = 'center';
        ctx.fillStyle = '#cbd7e8';
        ctx.font = '600 28px "Clash Display", "Switzer", Arial, sans-serif';
        ctx.fillText('DEPUTADO ESTADUAL', w / 2, h - 60);
      }

      // EU VOTO floating badge
      const vBadgeW = 220;
      const vBadgeH = 110;
      const vBadgeX = 50;
      const vBadgeY = topH + 30;

      ctx.shadowColor = 'rgba(0,0,0,0.35)';
      ctx.shadowBlur = 16;
      ctx.shadowOffsetY = 6;

      const vGrad = ctx.createLinearGradient(vBadgeX, vBadgeY, vBadgeX + vBadgeW, vBadgeY + vBadgeH);
      vGrad.addColorStop(0, '#3efa2a');
      vGrad.addColorStop(1, '#12b805');

      ctx.fillStyle = vGrad;
      ctx.beginPath();
      roundRect(ctx, vBadgeX, vBadgeY, vBadgeW, vBadgeH, 20);
      ctx.fill();
      ctx.shadowColor = 'transparent';

      drawStripeTexture(ctx, vBadgeX, vBadgeY, vBadgeW, vBadgeH, '#06194d', 12, 1, 0.07);

      ctx.textAlign = 'center';
      ctx.fillStyle = '#06194d';
      ctx.font = '700 22px "Clash Display", Arial, sans-serif';
      ctx.fillText('EU VOTO', vBadgeX + vBadgeW / 2, vBadgeY + 34);
      ctx.font = '700 48px "Clash Display", "Arial Black", sans-serif';
      ctx.fillText('55789', vBadgeX + vBadgeW / 2, vBadgeY + 76);

      ctx.restore();
    },
  },
];
